/**
 * Candlestick series renderer — registered as series type "candles".
 *
 * This is the GENERIC seam `docs/sdk/19-charts.md` promised: the chart core
 * never learns a domain shape, a renderer registers itself, and any app gets
 * OHLC rendering through the same `ctx.charts` contract. The data contract is
 * the usual `ChartPoint` — `y` carries the close (what the crosshair reads),
 * and `meta` carries `{ o, h, l, c }` for wicks, bodies, and the table.
 *
 * House rules honoured: colours come from the theme (up = status.good, down =
 * status.serious — never raw hex), 2px strokes, hover ring on the surface.
 */
import { withAlpha } from "./charts-color.mjs";
import { LINE_WIDTH, MARKER_MIN_SIZE, SURFACE_GAP } from "./series";
import type { ResolvedPoint, SeriesHit, SeriesPlotContext, SeriesRenderer } from "./types";

type Ohlc = { o: number; h: number; l: number; c: number };

function ohlcOf(p: ResolvedPoint): Ohlc | null {
  const m = p.meta as Ohlc | undefined;
  if (!m || ![m.o, m.h, m.l, m.c].every((v) => typeof v === "number" && Number.isFinite(v))) {
    return null;
  }
  return m;
}

/** Candle body width: 70% of the gap to the neighbour, clamped to legibility. */
function bodyWidth(points: ReadonlyArray<ResolvedPoint>, pc: SeriesPlotContext): number {
  if (pc.band) {
    const w = Number.isFinite(pc.band.bandWidth) ? pc.band.bandWidth : 8;
    return Math.max(3, Math.min(24, w * 0.7));
  }
  if (points.length >= 2) {
    const d = Math.abs(pc.posAt(points[1].x) - pc.posAt(points[0].x));
    return Math.max(3, Math.min(24, d * 0.7));
  }
  return 8;
}

export const candlesSeries: SeriesRenderer = {
  type: "candles",
  xKind: "linear",
  stackable: false,
  hover: "shared",
  pane: "main",

  extent(points) {
    // Auto-scale over the WHOLE bar (low..high), not just closes — a wick
    // clipped off screen is a lie the reader cannot detect.
    let min = Infinity;
    let max = -Infinity;
    for (const p of points) {
      const m = ohlcOf(p);
      if (!m) {
        if (p.y != null && Number.isFinite(p.y)) {
          min = Math.min(min, p.y);
          max = Math.max(max, p.y);
        }
        continue;
      }
      min = Math.min(min, m.l);
      max = Math.max(max, m.h);
    }
    return min === Infinity ? null : { min, max };
  },

  draw(points, pc) {
    const g = pc.ctx2d;
    const up = pc.theme.status.good;
    const down = pc.theme.status.serious;
    const w = bodyWidth(points, pc);

    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      const m = ohlcOf(p);
      if (!m) continue;
      const x = pc.band ? pc.band.center(i) : pc.posAt(p.x);
      const yH = pc.valueAt(m.h);
      const yL = pc.valueAt(m.l);
      const yO = pc.valueAt(m.o);
      const yC = pc.valueAt(m.c);
      const color = m.c >= m.o ? up : down;

      // Wick (high → low), 2px per the mark spec.
      g.lineWidth = LINE_WIDTH;
      g.strokeStyle = color;
      g.beginPath();
      g.moveTo(x, yH);
      g.lineTo(x, yL);
      g.stroke();

      // Body (open → close). A doji (o == c) still gets a visible line.
      const top = Math.min(yO, yC);
      const height = Math.max(Math.abs(yO - yC), LINE_WIDTH);
      g.fillStyle = color;
      g.fillRect(x - w / 2, top, w, height);
      // 2px surface ring where bodies would touch — keeps dense runs legible.
      if (w > 5) {
        g.lineWidth = 1;
        g.strokeStyle = withAlpha(pc.theme.surface, 0.6);
        g.strokeRect(x - w / 2 + 0.5, top + 0.5, w - 1, Math.max(height - 1, 1));
      }
    }

    // Hover: ring the close of the bar under the crosshair.
    if (pc.hoverIndex != null) {
      const p = points[pc.hoverIndex];
      const m = p ? ohlcOf(p) : null;
      if (p && m) {
        const x = pc.band ? pc.band.center(pc.hoverIndex) : pc.posAt(p.x);
        const y = pc.valueAt(m.c);
        g.beginPath();
        g.arc(x, y, MARKER_MIN_SIZE / 2, 0, Math.PI * 2);
        g.fillStyle = m.c >= m.o ? up : down;
        g.fill();
        g.lineWidth = SURFACE_GAP;
        g.strokeStyle = pc.theme.surface;
        g.stroke();
      }
    }
  },

  hitTest(points, px, _py, pc) {
    let best: SeriesHit | null = null;
    let bestD = Infinity;
    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      if (p.y == null && !ohlcOf(p)) continue;
      const x = pc.band ? pc.band.center(i) : pc.posAt(p.x);
      const d = Math.abs(x - px);
      if (d < bestD) {
        bestD = d;
        const m = ohlcOf(p);
        best = {
          index: i,
          px: x,
          py: pc.valueAt(m ? m.c : (p.y as number)),
          value: p.y,
        };
      }
    }
    return best;
  },

  columns(seriesLabel) {
    return [`${seriesLabel} open`, `${seriesLabel} high`, `${seriesLabel} low`, `${seriesLabel} close`];
  },
  cells(point) {
    const m = ohlcOf(point);
    if (!m) return [null, null, null, point.y];
    return [m.o, m.h, m.l, m.c];
  },
};
